import React from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { SONIC_LANGUAGES, findLanguageByCode, type LanguageOption } from '../constants/languages'
import { FlagIcon } from './ui/flag-icon'
import { createPortal } from 'react-dom'

export interface LanguagePickerProps {
  value: string
  onChange: (iso: string) => void | Promise<void>
  onBeforeChange?: (nextIso: string) => Promise<boolean> | boolean
  className?: string
  size?: 'sm' | 'md'
  label?: string
  disabled?: boolean
  mode?: 'full' | 'compact'
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  value,
  onChange,
  onBeforeChange,
  className = '',
  size = 'sm',
  label,
  disabled = false,
  mode = 'full'
}) => {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const [portalStyle, setPortalStyle] = React.useState<{ top: number; left: number; width: number } | null>(null)

  const languages = SONIC_LANGUAGES
  const current = React.useMemo(() => findLanguageByCode(value?.toLowerCase()) || languages[0], [value, languages])

  const filtered = React.useMemo(() => {
    if (!query.trim()) return languages
    const q = query.trim().toLowerCase()
    return languages.filter(l => l.name.toLowerCase().includes(q) || l.localName.toLowerCase().includes(q) || l.code.toLowerCase().includes(q))
  }, [languages, query])

  const handleSelect = async (lang: LanguageOption) => {
    if (disabled) return
    if (lang.code === value.toLowerCase()) { setOpen(false); return }
    if (onBeforeChange) {
      const ok = await onBeforeChange(lang.code)
      if (!ok) return
    }
    await onChange(lang.code)
    setOpen(false)
  }

  const computePosition = React.useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const width = mode === 'compact' ? Math.max(rect.width, 240) : rect.width
    setPortalStyle({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width })
  }, [mode])

  const toggle = () => {
    if (disabled) return
    setOpen(o => {
      const next = !o
      if (next) {
        computePosition()
        setQuery('')
        setTimeout(() => inputRef.current?.focus(), 0)
      }
      return next
    })
  }

  React.useEffect(() => {
    if (!open) return
    const handle = () => computePosition()
    window.addEventListener('resize', handle)
    window.addEventListener('scroll', handle, true)
    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, true)
    }
  }, [open, computePosition])

  const sizeClasses = size === 'sm' ? 'h-9 text-xs px-2' : 'h-10 text-sm px-3'
  const isCompact = mode === 'compact'
  const compactSizeClasses = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10'

  return (
    <div className={`relative ${className}`}>
      {label && <label className="mb-1 block text-sm font-medium text-foreground">{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        className={`flex ${isCompact ? compactSizeClasses : 'w-full'} items-center ${isCompact ? 'justify-center' : 'justify-between'} rounded-lg border border-black/10 bg-white ${isCompact ? '' : sizeClasses} gap-2 text-left transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black/5 cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={isCompact ? `Language: ${current.name}` : undefined}
        title={isCompact ? current.name : undefined}
      >
        <span className="flex items-center gap-2 min-w-0">
          <FlagIcon countryCode={current.flag} className="rounded-sm" style={{ width: '16px', height: '12px' }} />
          {!isCompact && <span className="truncate">{current.name}</span>}
        </span>
        {!isCompact && <ChevronDown className={`h-4 w-4 text-foreground/60 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && portalStyle && createPortal(
        <>
          <div className="fixed inset-0 z-[990]" onClick={() => setOpen(false)} />
          <div
            className="z-[1000] absolute"
            style={{ position: 'absolute', top: portalStyle.top, left: portalStyle.left, width: portalStyle.width }}
          >
            <div className="rounded-lg border border-black/10 bg-white shadow-lg">
              <div className="p-2 border-b border-black/5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-foreground/40" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search"
                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-black/10 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto py-1 z-100">
                {filtered.length === 0 && (
                  <div className="px-3 py-2 text-xs text-foreground/60">No languages</div>
                )}
                {filtered.map(l => {
                  const selected = l.code === value.toLowerCase()
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => handleSelect(l)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 cursor-pointer ${selected ? 'bg-brand-50 text-brand-700' : 'text-foreground'}`}
                    >
                      <FlagIcon countryCode={l.flag} className="rounded-sm" style={{ width: '16px', height: '12px' }} />
                      <span className="truncate">{l.name}</span>
                      {selected && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-500" />}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

export default LanguagePicker
